/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * ContactsListPage -- R13 web Round 1.
 *
 * Owner-area contacts list. Search + filter + click-into-detail.
 * Hits GET /api/connect/contacts (shared with mobile).
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import connectApi from '../../services/connectApi';

const SKD_CYAN = '#06b6d4';
const SKD_CYAN_DARK = '#0e7490';

const REPRESENTS_OPTIONS = [
  { v: '',                   label: 'All' },
  { v: 'individual',         label: 'Individual' },
  { v: 'company',            label: 'Company' },
  { v: 'university_college', label: 'University / College' },
  { v: 'institute',          label: 'Training Institute' }
];

const DOMAIN_OPTIONS = [
  { v: '',             label: 'All' },
  { v: 'wms',          label: 'Warehousing / WMS' },
  { v: 'iseries',      label: 'IBM i / iSeries' },
  { v: 'civil',        label: 'Civil Construction' },
  { v: 'apparel',      label: 'Apparel / Stitching' },
  { v: 'erpnext',      label: 'ERPNext' },
  { v: 'general',      label: 'General' }
];

export default function ContactsListPage() {
  const [filters, setFilters] = useState({ q: '', represents: '', domain: '', event: '' });
  const [events, setEvents] = useState([]);
  const [contacts, setContacts] = useState(null); // null = loading
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const data = await connectApi.listContacts(filters);
      // Server returns `{ contacts: [...], total }` on mobile; some
      // older versions return the array directly. Normalise.
      const list = Array.isArray(data) ? data : (data && data.contacts) || [];
      setContacts(list);
    } catch (e) {
      setErr(e.message || 'Could not load contacts.');
      setContacts([]);
    } finally { setBusy(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const data = await connectApi.listEvents();
        setEvents(Array.isArray(data) ? data : (data && data.events) || []);
      } catch (_) { /* fail-soft */ }
    })();
  }, []);

  const setFilter = (key, v) => setFilters((prev) => ({ ...prev, [key]: v }));

  return (
    <section>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
        <h1 style={styles.h1}>Contacts</h1>
        <span style={{ color: '#64748b', fontSize: 13 }}>
          {contacts === null ? '' : `${contacts.length} on this page`}
        </span>
      </div>

      <div style={styles.filterBar}>
        <input
          placeholder="Search name / org / phone / email"
          value={filters.q}
          onChange={(e) => setFilter('q', e.target.value)}
          style={{ ...styles.input, flex: 2, minWidth: 200 }}
        />
        <select value={filters.represents} onChange={(e) => setFilter('represents', e.target.value)} style={styles.select}>
          {REPRESENTS_OPTIONS.map((o) => <option key={o.v || 'all'} value={o.v}>{o.label}</option>)}
        </select>
        <select value={filters.domain} onChange={(e) => setFilter('domain', e.target.value)} style={styles.select}>
          {DOMAIN_OPTIONS.map((o) => <option key={o.v || 'all'} value={o.v}>{o.label}</option>)}
        </select>
        {events.length > 0 && (
          <select value={filters.event} onChange={(e) => setFilter('event', e.target.value)} style={styles.select}>
            <option value="">All events</option>
            {events.map((e) => (<option key={e.id} value={e.id}>{e.name}</option>))}
          </select>
        )}
      </div>

      {err && <div style={styles.errorBox}>{err}</div>}

      {contacts === null && <div style={styles.muted}>Loading…</div>}
      {contacts && contacts.length === 0 && !busy && (
        <div style={styles.emptyCard}>
          No contacts match these filters. Try widening the search, or add a
          new one from the mobile app (web capture surfaces land in Round 3).
        </div>
      )}

      {contacts && contacts.length > 0 && (
        <div style={{ display: 'grid', gap: 8 }}>
          {contacts.map((c) => (
            <Link
              key={c.id || c._id}
              to={`/connect/contacts/${encodeURIComponent(c.id || c._id)}`}
              style={styles.row}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>
                  {c.full_name || <em style={{ color: '#94a3b8' }}>(no name)</em>}
                </div>
                <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>
                  {[c.organisation, c.role].filter(Boolean).join(' · ')}
                </div>
                <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>
                  {[c.phone, c.email].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                {c.represents && <Chip>{_prettyRepresents(c.represents)}</Chip>}
                {c.domain && <Chip>{_prettyDomain(c.domain)}</Chip>}
                {c.opted_out && <Chip tone="warn">opted out</Chip>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function Chip({ children, tone }) {
  const bg = tone === 'warn' ? '#fef3c7' : '#e0f2fe';
  const fg = tone === 'warn' ? '#92400e' : SKD_CYAN_DARK;
  return (
    <span style={{
      fontSize: 11, padding: '2px 8px', borderRadius: 999,
      background: bg, color: fg, whiteSpace: 'nowrap'
    }}>{children}</span>
  );
}

function _prettyRepresents(r) {
  const map = {
    individual: 'Individual',
    company: 'Company',
    university_college: 'University',
    institute: 'Institute'
  };
  return map[r] || r;
}
function _prettyDomain(d) {
  const map = {
    wms: 'Warehousing', iseries: 'iSeries', civil: 'Civil',
    apparel: 'Apparel', erpnext: 'ERPNext', general: 'General'
  };
  return map[d] || d;
}

const styles = {
  h1: { fontSize: 22, color: '#0f172a', margin: 0 },
  filterBar: {
    display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14,
    padding: 12, background: 'white', border: '1px solid #e2e8f0', borderRadius: 10
  },
  input: {
    padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
    fontSize: 14, fontFamily: 'inherit'
  },
  select: {
    padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1',
    fontSize: 14, fontFamily: 'inherit', minWidth: 140, background: 'white'
  },
  row: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 14px', borderRadius: 10,
    background: 'white', border: '1px solid #e2e8f0',
    color: '#0f172a', textDecoration: 'none'
  },
  emptyCard: {
    padding: 20, background: 'white', border: '1px solid #e2e8f0', borderRadius: 10,
    color: '#64748b', fontSize: 14
  },
  muted: { color: '#94a3b8', padding: 20, textAlign: 'center' },
  errorBox: {
    padding: 10, background: '#fef2f2', color: '#991b1b',
    borderRadius: 8, border: '1px solid #fecaca', marginBottom: 10, fontSize: 13
  }
};
